import { useEffect, useRef, useState } from 'react';
import type { LevelConfig, LevelInstance, LevelResult } from '@dave/level-contract';
import { isCompatible, CONTRACT_VERSION } from '@dave/level-contract';
import { loadAssetsCatalog } from '@/api/assetsCatalog';
import type { LevelConfigDto } from '@/api/dto';
import { CANVAS_THEME } from '@/meta/canvasTheme';
import { withBgUrl } from '@/meta/worlds';
import { assetCacheBust } from '@/config/stage';

// Общий async-скелет хоста плеера (PlayScreen + WildcardScreen): очистка mount,
// cancelled-гард, проверка playerType, dynamic import плеера + isCompatible,
// media.baseUrl из каталога ассетов, сборка LevelConfig, createLevel, destroy.
// Экранные ветки (гейт/сюжет/persist/пейволл) НАМЕРЕННО не здесь — они живут
// в PlayScreen (loadConfig/onComplete), чтобы хук не превратился в конфиг-комбайн.
// RunOverlay конструктора хук не использует: у него другой жизненный цикл
// (перезапуск по doc, свой toPlayable/bgUrl) — вердикт аудита 2026-07-16.

export type LevelHostPhase = 'loading' | 'playing' | 'result' | 'error';

/** Ручки скелета для экранного loadConfig: живой cancelled-флаг эффекта и
 *  перевод в экранную фазу (locked/paywall/story) вместо монтирования плеера. */
export interface LevelHostCtl<Extra extends string = never> {
  cancelled(): boolean;
  setPhase(phase: LevelHostPhase | Extra): void;
}

export interface LevelHostOptions<Extra extends string = never> {
  /** false — эффект ждёт (нет slug / auth bootstrap не определил, кто мы) */
  enabled?: boolean;
  /** ключ перезапуска эффекта (levelId/slug + всё, что должно пересоздать плеер) */
  key: string;
  /** ВЫБОР ГАРДЕРОБА (LevelConfig.skin/hat): id облика и шапки из профиля игрока. Экран
   *  решает, откуда их взять — боевой из прогресса (открываются за звёзды мира), стенд из
   *  адреса. Пусто — житель мира уровня и без шапки. */
  skin?: string;
  hat?: string;
  /** LevelConfig.capabilities: что хост разрешает плееру сверх обычной игры.
   *  Сейчас одна — 'record' (покадровая съёмка трейлера, players/launch-route recorder.ts);
   *  просит её только дев-экран стенда. */
  capabilities?: string[];
  /** стартовые громкости LevelConfig.settings; читается в момент сборки конфига */
  getVolumes(): { music: number; sfx: number };
  /** экранная часть загрузки: достать LevelConfigDto (плюс свои гейты/ролики).
   *  null = экран сам выставил фазу через ctl.setPhase — плеер не монтируем.
   *  Ошибка (throw) → фаза 'error' с текстом ошибки. */
  loadConfig(ctl: LevelHostCtl<Extra>): Promise<LevelConfigDto | null>;
  /** первый onComplete плеера: экран может увести фазу в свою (after-ролики)
   *  и запустить persist; вернуть фазу для показа. По умолчанию — 'result'. */
  onComplete?(r: LevelResult): LevelHostPhase | Extra;
}

export function useLevelHost<Extra extends string = never>(opts: LevelHostOptions<Extra>) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<LevelInstance | null>(null);
  const [phase, setPhase] = useState<LevelHostPhase | Extra>('loading');
  const [result, setResult] = useState<LevelResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0); // «ещё раз» пересоздаёт плеер

  // колбэки экрана — свежие на момент вызова, но НЕ в deps эффекта,
  // иначе каждый рендер экрана пересоздавал бы плеер
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const { enabled = true, key } = opts;

  useEffect(() => {
    if (!enabled || !mountRef.current) return;
    let instance: LevelInstance | null = null;
    let cancelled = false;
    // защита от повторного onComplete: контракт не запрещает плееру позвать его
    // дважды; без гвардии второй вызов дублирует экранные эффекты (POST/ролики)
    let completed = false;
    const mount = mountRef.current;
    mount.innerHTML = '';
    setPhase('loading');
    setResult(null);
    setError(null);

    const ctl: LevelHostCtl<Extra> = { cancelled: () => cancelled, setPhase };

    // Ни бандл плеера, ни каталог ассетов не зависят от СОДЕРЖИМОГО конфига —
    // стартуем их сразу, параллельно с loadConfig. Раньше это были три сетевых
    // ожидания подряд (конфиг → чанк плеера → каталог), и первый байт арта уходил
    // на ~1.9 с позже (замер на dev, 2026-07-29). Плеер сейчас ровно один, так что
    // предзагрузить его до проверки playerType — не промах, а тот же самый чанк.
    // `hold` гасит unhandled rejection: настоящая обработка — на await ниже.
    const hold = <T>(p: Promise<T>): Promise<T> => { p.catch(() => {}); return p; };
    const playerP = hold(import('@dave/player-launch-route'));
    const catalogP = hold(loadAssetsCatalog());

    void (async () => {
      try {
        const dto = await optsRef.current.loadConfig(ctl);
        if (cancelled || !dto) return; // null — экран сам выставил свою фазу

        if (dto.playerType !== 'launch-route')
          throw new Error(`неизвестный плеер: ${dto.playerType}`);
        const player = await playerP;
        if (!isCompatible(CONTRACT_VERSION, player.contractVersion))
          throw new Error('несовместимая версия плеера — обнови страницу');
        if (cancelled) return;

        // медиа уровня — S3/CDN: baseUrl плеера из публичного каталога ассетов
        // (модульный кэш — если экран уже тянул каталог, второго фетча не будет)
        let catalog: Awaited<ReturnType<typeof loadAssetsCatalog>> | null = null;
        try { catalog = await catalogP; } catch { /* ошибка ниже, единая */ }
        if (cancelled) return;
        const mediaBaseUrl = catalog?.players.find((p) => p.playerType === dto.playerType)?.baseUrl;
        if (!mediaBaseUrl) {
          setError('Медиа уровня недоступны — попробуй позже');
          setPhase('error');
          return;
        }

        const volumes = optsRef.current.getVolumes();
        const config: LevelConfig = {
          levelId: dto.levelId,
          playerType: dto.playerType,
          // фон — с медиа-корня каталога той же функцией, что в «Запустить» конструктора
          data: withBgUrl(dto.data, catalog?.mediaBaseUrl),
          mode: 'play',
          locale: 'ru',
          settings: {
            music: volumes.music,
            sfx: volumes.sfx,
            reducedMotion: typeof window.matchMedia === 'function'
              && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            input: 'pointer',
          },
          theme: CANVAS_THEME,
          seed: dto.seed,
          // cacheBust — только в стадии development (config/stage.ts): перезалитый
          // на S3 арт виден сразу, без ручного сброса кэша браузера
          media: { baseUrl: mediaBaseUrl, cacheBust: assetCacheBust() },
          ...(optsRef.current.skin ? { skin: optsRef.current.skin } : {}),
          ...(optsRef.current.hat ? { hat: optsRef.current.hat } : {}),
          ...(optsRef.current.capabilities?.length
            ? { capabilities: optsRef.current.capabilities }
            : {}),
        };

        instance = player.createLevel(mount, config, {
          onReady() {
            if (cancelled) return;
            setPhase('playing');
            instance?.start();
          },
          onProgress() {},
          onComplete(r) {
            if (cancelled || completed) return; // повторный вызов — no-op
            completed = true;
            setResult(r);
            setPhase(optsRef.current.onComplete?.(r) ?? 'result');
          },
          onError(e) {
            if (cancelled) return;
            setError(`${e.code}: ${e.message}`);
            setPhase('error');
          },
          onAnalytics() {}, // M5: очередь ingest с префиксом player.
          onRequestQuit() {},
        });
        instanceRef.current = instance;
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      instanceRef.current = null;
      instance?.destroy();
    };
  }, [enabled, key, attempt]);

  return {
    mountRef,
    /** живой инстанс плеера — для setVolumes живьём из настроек экрана */
    instanceRef,
    phase,
    /** экранные переходы фаз вне скелета (после after-роликов и т.п.) */
    setPhase,
    result,
    error,
    /** «ещё раз»: перезапуск эффекта → пересоздание плеера */
    restart: () => setAttempt((a) => a + 1),
  };
}
